import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { publicApi, Demonstration, Feedback, getMediaUrl } from '../services/api';
import MediaDialog from '../components/MediaDialog';
import MembershipCard from '../components/MembershipCard';


interface ProfileForm {
  name: string;
}

const ProfilePage: React.FC = () => {
  const { user, updateName } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [demonstrations, setDemonstrations] = useState<Demonstration[]>([]);
  const [demosLoading, setDemosLoading] = useState(true);
  const [selectedDemo, setSelectedDemo] = useState<Demonstration | null>(null);
  const [showDemoMenu, setShowDemoMenu] = useState(false);
  const [selfNoteText, setSelfNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [demoFeedback, setDemoFeedback] = useState<Feedback[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting, isDirty } } = useForm<ProfileForm>({
    defaultValues: { name: user?.name || '' },
  });

  useEffect(() => {
    const loadDemos = async () => {
      try {
        const data = await publicApi.demonstrations.getMine();
        setDemonstrations(data);
      } catch {
        // silently fail - user may not have a dancer profile
      } finally {
        setDemosLoading(false);
      }
    };
    loadDemos();
  }, []);

  // Load feedback when a demo is selected
  useEffect(() => {
    if (!selectedDemo) {
      setDemoFeedback([]);
      return;
    }
    const loadFeedback = async () => {
      setFeedbackLoading(true);
      try {
        const fb = await publicApi.feedback.getByDemonstration(selectedDemo.id);
        setDemoFeedback(fb);
      } catch {
        setDemoFeedback([]);
      } finally {
        setFeedbackLoading(false);
      }
    };
    loadFeedback();
  }, [selectedDemo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data: ProfileForm) => {
    setError(null);
    setSuccess(null);
    try {
      await updateName(data.name);
      setSuccess('Profile updated successfully.');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    }
  };

  const handleTogglePublic = async () => {
    if (!selectedDemo) return;
    try {
      const goingPrivate = selectedDemo.isPublic;
      const data: any = { isPublic: !selectedDemo.isPublic };
      // Downgrade "all" feedback to "instructors" when going private
      if (goingPrivate && selectedDemo.feedbackAudience === 'all') {
        data.feedbackAudience = 'instructors';
      }
      const updated = await publicApi.demonstrations.update(selectedDemo.id, data);
      setSelectedDemo({ ...selectedDemo, ...updated });
      setDemonstrations(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
    } catch {
      alert('Failed to update visibility.');
    }
  };

  // Cycle feedback loop based on privacy:
  //   Private: Closed → Instructors → Closed
  //   Public:  Closed → Instructors → Members → Closed
  const handleCycleFeedback = async () => {
    if (!selectedDemo) return;
    try {
      let updated: Demonstration;
      const isOpen = selectedDemo.feedbackOpen;
      const audience = selectedDemo.feedbackAudience;
      const isPrivate = !selectedDemo.isPublic;

      if (!audience || !isOpen) {
        // Closed → Instructors
        updated = await publicApi.demonstrations.requestFeedback(selectedDemo.id, 'instructors');
      } else if (audience === 'instructors' && isOpen && !isPrivate) {
        // Instructors → Members (public only)
        updated = await publicApi.demonstrations.requestFeedback(selectedDemo.id, 'all');
      } else {
        // Instructors (private) → Closed, or Members → Closed
        updated = await publicApi.demonstrations.closeFeedback(selectedDemo.id);
      }

      setSelectedDemo({ ...selectedDemo, ...updated });
      setDemonstrations(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
    } catch {
      alert('Failed to update feedback settings.');
    }
  };

  const handleSubmitSelfNote = async () => {
    if (!selectedDemo || !selfNoteText.trim()) return;
    setSubmittingNote(true);
    try {
      const fb = await publicApi.feedback.addFromDancer({
        demonstrationId: selectedDemo.id,
        content: selfNoteText.trim(),
      });
      setDemoFeedback(prev => [fb, ...prev]);
      setSelfNoteText('');
    } catch {
      alert('Failed to add note.');
    } finally {
      setSubmittingNote(false);
    }
  };

  // Group demonstrations by their parent (move OR pattern). The non-null FK is the
  // group key. Move and pattern groups can coexist; the per-card type pill below
  // disambiguates when the dancer has both.
  type DemoGroup = {
    type: 'MOVE' | 'PATTERN';
    title: string;
    href: string;
    demos: Demonstration[];
  };
  const demoGroups = demonstrations.reduce<Record<string, DemoGroup>>((acc, demo) => {
    if (demo.demonstrationType === 'PATTERN' && demo.patternId) {
      const key = `pattern-${demo.patternId}`;
      if (!acc[key]) {
        acc[key] = {
          type: 'PATTERN',
          title: demo.pattern?.title || 'Unknown Pattern',
          href: `/library/patterns/${demo.pattern?.slug || demo.patternId}`,
          demos: [],
        };
      }
      acc[key].demos.push(demo);
    } else if (demo.moveId) {
      const key = `move-${demo.moveId}`;
      if (!acc[key]) {
        acc[key] = {
          type: 'MOVE',
          title: demo.move?.title || 'Unknown Move',
          href: `/library/moves/${demo.moveId}`,
          demos: [],
        };
      }
      acc[key].demos.push(demo);
    }
    return acc;
  }, {});

  const hasMixedDemoTypes = (() => {
    let sawMove = false;
    let sawPattern = false;
    for (const g of Object.values(demoGroups)) {
      if (g.type === 'MOVE') sawMove = true;
      else sawPattern = true;
      if (sawMove && sawPattern) return true;
    }
    return false;
  })();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="main-content">
      <div className="auth-page profile-layout">
        <div className="form-container auth-form-container">
          <h2>My Profile</h2>
          {error && <p className="auth-error">{error}</p>}
          {success && <p className="auth-success">{success}</p>}
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
              />
            </div>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                type="text"
                className={errors.name ? 'error' : ''}
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting || !isDirty}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
          <MembershipCard />
        </div>

        {/* My Demonstrations */}
        <div className="form-container auth-form-container profile-demos-container">
          <h2>My Demonstrations</h2>
          {demosLoading ? (
            <p className="profile-empty-hint">Loading demonstrations...</p>
          ) : Object.keys(demoGroups).length === 0 ? (
            <p className="profile-empty-hint">You haven't submitted any demonstrations yet.</p>
          ) : (
            Object.entries(demoGroups).map(([key, { type, title, href, demos }]) => (
              <div key={key} className="profile-demo-group">
                <h3 className="profile-demo-group-title">
                  <Link to={href}>{title}</Link>
                  {hasMixedDemoTypes && (
                    <span className={`badge profile-demo-type-badge ${type === 'PATTERN' ? 'pattern' : 'move'}`}>
                      {type === 'PATTERN' ? 'Pattern' : 'Move'}
                    </span>
                  )}
                </h3>
                <div className="profile-demo-group-items">
                  {demos.map((demo) => (
                    <button key={demo.id} type="button" className="profile-demo-card" onClick={() => { setSelectedDemo(demo); setSelfNoteText(''); }}>
                      <div className="profile-demo-thumbnail">
                        <video
                          src={demo.videoUrl.startsWith('http') ? demo.videoUrl : getMediaUrl(demo.videoUrl)}
                          poster={demo.thumbnail ? (demo.thumbnail.startsWith('http') ? demo.thumbnail : getMediaUrl(demo.thumbnail)) : undefined}
                          playsInline
                          muted
                        />
                        {/* Privacy icon on card */}
                        <span className="profile-demo-privacy-icon" role="img" aria-label={demo.isPublic ? 'Public' : 'Private'} title={demo.isPublic ? 'Public' : 'Private'}>
                          {demo.isPublic ? '🌐' : '🔒'}
                        </span>
                      </div>
                      <div className="profile-demo-meta">
                        {demo.caption && <p className="profile-demo-caption">{demo.caption}</p>}
                        <div className="profile-demo-footer">
                          <span>{formatDate(demo.recordedAt || demo.createdAt)}</span>
                          <span className={`profile-demo-status ${demo.isApproved ? 'approved' : 'pending'}`}>
                            {demo.isApproved ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Demo playback modal */}
      {selectedDemo && (
        <MediaDialog
          src={getMediaUrl(selectedDemo.videoUrl)}
          poster={selectedDemo.thumbnail ? getMediaUrl(selectedDemo.thumbnail) : undefined}
          title="Your demonstration"
          onClose={() => { setSelectedDemo(null); setShowDemoMenu(false); }}
        >
          <div className="profile-demo-modal-body">
            <div className="profile-demo-modal-header">
              <div>
                <h3>
                  {selectedDemo.demonstrationType === 'PATTERN'
                    ? (selectedDemo.pattern?.title || 'Unknown Pattern')
                    : (selectedDemo.move?.title || 'Unknown Move')}
                </h3>
                {selectedDemo.caption && (
                  <p className="demonstration-modal-caption">{selectedDemo.caption}</p>
                )}
                <div className="profile-demo-modal-badges">
                  {/* Approval status */}
                  <span className={`profile-demo-modal-badge ${selectedDemo.isApproved ? 'approved' : 'pending'}`}>
                    {selectedDemo.isApproved ? 'Approved' : 'Pending Review'}
                  </span>
                  {/* Privacy badge */}
                  <button
                    type="button"
                    onClick={handleTogglePublic}
                    className={`profile-demo-modal-badge clickable ${selectedDemo.isPublic ? 'public' : 'muted'}`}
                    title="Click to toggle visibility"
                  >
                    {selectedDemo.isPublic ? '🌐 Public' : '🔒 Private'}
                    {selectedDemo.isPublic && !selectedDemo.isApproved && ' (Pending Approval)'}
                  </button>
                  {/* Feedback loop badge — clickable cycle */}
                  <button
                    type="button"
                    onClick={handleCycleFeedback}
                    className={`profile-demo-modal-badge clickable ${
                      !selectedDemo.feedbackAudience || !selectedDemo.feedbackOpen
                        ? 'muted'
                        : selectedDemo.feedbackAudience === 'instructors' ? 'instructors' : 'members'
                    }`}
                    title="Click to cycle feedback mode"
                  >
                    {!selectedDemo.feedbackAudience || !selectedDemo.feedbackOpen
                      ? '🚫 Closed Feedback Loop'
                      : selectedDemo.feedbackAudience === 'instructors'
                        ? '👨‍🏫 Instructor Only Feedback Loop'
                        : '👥 Member Feedback Loop'}
                  </button>
                </div>
              </div>
              <div className="demo-menu-container">
                <button className="demo-menu-trigger" aria-label="More options" onClick={() => setShowDemoMenu(!showDemoMenu)}>
                  &hellip;
                </button>
                {showDemoMenu && (
                  <div className="demo-menu-dropdown">
                    {/* Delete */}
                    <button
                      className="demo-menu-item demo-menu-item-danger"
                      onClick={async () => {
                        if (!window.confirm('Delete this demonstration?')) return;
                        try {
                          await publicApi.demonstrations.delete(selectedDemo.id);
                          setDemonstrations(prev => prev.filter(d => d.id !== selectedDemo.id));
                          setSelectedDemo(null);
                          setShowDemoMenu(false);
                        } catch {
                          alert('Failed to delete demonstration.');
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Feedback list */}
            {feedbackLoading ? (
              <p className="profile-feedback-loading">Loading feedback...</p>
            ) : demoFeedback.length > 0 && (
              <div className="profile-feedback-section">
                <h4 className="profile-feedback-title">Feedback ({demoFeedback.length})</h4>
                <div className="demo-modal-feedback-list profile-feedback-list">
                  {demoFeedback.map((fb) => (
                    <div key={fb.id} className="demo-modal-feedback-item profile-feedback-item">
                      <span className="feedback-author">
                        {fb.instructor?.name || fb.dancer?.name}
                        {fb.instructor && <span className="instructor-badge profile-feedback-instructor-badge">Instructor</span>}
                      </span>
                      <p>{fb.content}</p>
                      <span className="profile-feedback-date">{formatDate(fb.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Self-note input (always visible for owner) */}
            <div className="profile-self-note-row">
              <input
                type="text"
                placeholder="Add a note..."
                aria-label="Add a note"
                value={selfNoteText}
                onChange={(e) => setSelfNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitSelfNote(); }}
                className="profile-self-note-input"
                disabled={submittingNote}
              />
              <button
                onClick={handleSubmitSelfNote}
                disabled={submittingNote || !selfNoteText.trim()}
                className="profile-self-note-submit"
              >
                {submittingNote ? '...' : 'Add'}
              </button>
            </div>
          </div>
        </MediaDialog>
      )}
    </div>
  );
};

export default ProfilePage;
